import React, { useEffect, useMemo, useRef, useState } from 'react'
import RegionOverlay, { DisplayRegion } from './RegionOverlay'

export type ImageRegion = {
  id: string
  pageNumber: number
  x1: number
  y1: number
  x2: number
  y2: number
  validated?: boolean
}

type Props = {
  enabled: boolean
  imageSrc: string
  rawWidth: number
  rawHeight: number
  regions: ImageRegion[]
  readOnly?: boolean
  onRegionsChange: (regions: ImageRegion[]) => void
}

type RenderRect = {
  left: number
  top: number
  width: number
  height: number
}

const ImageReactAnnotator: React.FC<Props> = ({
  enabled,
  imageSrc,
  rawWidth,
  rawHeight,
  regions,
  readOnly = false,
  onRegionsChange,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const imageRef = useRef<HTMLImageElement | null>(null)
  const [renderRect, setRenderRect] = useState<RenderRect>({ left: 0, top: 0, width: 0, height: 0 })

  useEffect(() => {
    const updateSize = () => {
      const container = containerRef.current
      const img = imageRef.current
      if (!img || !container || !rawWidth || !rawHeight) return

      const containerWidth = container.clientWidth
      const containerHeight = container.clientHeight
      if (!containerWidth || !containerHeight) {
        setRenderRect({ left: 0, top: 0, width: 0, height: 0 })
        return
      }

      const imageRatio = rawWidth / rawHeight
      const containerRatio = containerWidth / containerHeight

      let renderedWidth = containerWidth
      let renderedHeight = containerHeight

      if (imageRatio > containerRatio) {
        renderedHeight = containerWidth / imageRatio
      } else {
        renderedWidth = containerHeight * imageRatio
      }

      setRenderRect({
        left: (containerWidth - renderedWidth) / 2,
        top: (containerHeight - renderedHeight) / 2,
        width: renderedWidth,
        height: renderedHeight,
      })
    }

    updateSize()
    const timer = window.setInterval(updateSize, 400)
    window.addEventListener('resize', updateSize)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('resize', updateSize)
    }
  }, [imageSrc, rawHeight, rawWidth])

  const displayRegions = useMemo<DisplayRegion[]>(() => {
    const { width, height } = renderRect
    if (!width || !height || !rawWidth || !rawHeight) return []

    const sx = width / rawWidth
    const sy = height / rawHeight

    return regions.map((region) => ({
      id: region.id,
      left: Math.min(region.x1, region.x2) * sx,
      top: Math.min(region.y1, region.y2) * sy,
      width: Math.abs(region.x2 - region.x1) * sx,
      height: Math.abs(region.y2 - region.y1) * sy,
      validated: region.validated,
    }))
  }, [renderRect, rawHeight, rawWidth, regions])

  const handleRegionsChange = (nextDisplayRegions: DisplayRegion[]) => {
    const { width, height } = renderRect
    if (!width || !height || !rawWidth || !rawHeight) return

    const sx = rawWidth / width
    const sy = rawHeight / height

    const mapped: ImageRegion[] = nextDisplayRegions.map((region) => {
      const x1 = Math.max(0, Math.min(region.left * sx, rawWidth))
      const y1 = Math.max(0, Math.min(region.top * sy, rawHeight))
      const x2 = Math.max(0, Math.min((region.left + region.width) * sx, rawWidth))
      const y2 = Math.max(0, Math.min((region.top + region.height) * sy, rawHeight))

      return {
        id: region.id,
        pageNumber: 1,
        x1,
        y1,
        x2,
        y2,
        validated: region.validated,
      }
    })

    onRegionsChange(mapped)
  }

  if (!imageSrc) return null

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {import.meta.env.DEV && (
        <div
          id="sod-image-debug-panel"
          style={{
            position: 'fixed',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(0,0,0,0.82)',
            color: '#fff',
            padding: '5px 20px',
            fontSize: '12px',
            lineHeight: '1.6',
            pointerEvents: 'none',
            zIndex: 10001,
            borderRadius: '6px',
            whiteSpace: 'nowrap',
          }}
        >
          Hover to see coordinates...
        </div>
      )}
      <img
        ref={imageRef}
        src={imageSrc}
        alt="Annotation target"
        style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
      />
      <div
        style={{
          position: 'absolute',
          left: `${renderRect.left}px`,
          top: `${renderRect.top}px`,
          width: `${renderRect.width}px`,
          height: `${renderRect.height}px`,
          zIndex: 10,
          overflow: 'hidden',
        }}
        onMouseMove={(e) => {
          const rect = e.currentTarget.getBoundingClientRect()
          const pxLeft = e.clientX - rect.left
          const pxTop = e.clientY - rect.top

          if (import.meta.env.DEV && renderRect.width && renderRect.height) {
            const sx = rawWidth / renderRect.width
            const sy = rawHeight / renderRect.height
            const imgX = pxLeft * sx
            const imgY = pxTop * sy

            const debugEl = document.getElementById('sod-image-debug-panel')
            if (debugEl) {
              debugEl.innerHTML = `<div style="display:grid;grid-template-columns:auto 1fr auto 1fr;gap:2px 20px;align-items:baseline"><span style="color:#9ca3af">UI px</span><span>X: ${pxLeft.toFixed(1)}, Y: ${pxTop.toFixed(1)}</span><span style="color:#9ca3af">Overlay</span><span>${renderRect.width.toFixed(0)} × ${renderRect.height.toFixed(0)}</span><span style="color:#9ca3af">Raw</span><span>${rawWidth} × ${rawHeight}</span><span style="color:#9ca3af">Image space</span><span>X: ${imgX.toFixed(1)}, Y: ${imgY.toFixed(1)}</span></div>`
            }
          }
        }}
      >
        <RegionOverlay
          enabled={enabled}
          readOnly={readOnly}
          width={renderRect.width}
          height={renderRect.height}
          regions={displayRegions}
          onRegionsChange={handleRegionsChange}
        />
      </div>
    </div>
  )
}

export default ImageReactAnnotator
